import React, { useCallback, useState } from 'react';
import ShowEntityData from '../../../Elements/ShowEntityData';
import ErrorMessage from '../../../Elements/ErrorMessage';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import GeneralEditWizzardBaseSideBySide from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseSideBySideFigma';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import ContactService from '../../../Services/contactService';
import OpportunitySecurityChecklistService from '../../../Services/opportunitySecurityChecklistService';
import OpportunitySecurityChecklistItem from '../../../Domain/OpportunitySecurityChecklistItem';
import ContractImportItem from '../../../Domain/ContractImportItem';
import OpportunityStatusEnum from '../../../Enums/OpportunityStatusEnum';
import ImportContractUploadWindowDescription from './ImportContractUploadWindowDescription';
import ImportContractReviewWindowDescription from './ImportContractReviewWindowDescription';
import ImportScheduleEdReviewWindowDescription from './ImportScheduleEdReviewWindowDescription';
import EditOpportunitySecurityChecklistWindowDescription from './EditOpportunitySecurityChecklistWindowDescription';
import ImportCoveredChecklistFieldsArray from '../../../Enums/ImportCoveredChecklistFieldsArray';
import ScheduleEdImportCoveredChecklistFieldsArray from '../../../Enums/ScheduleEdImportCoveredChecklistFieldsArray';
import ChooseChecklistSourcePanel from './ChooseChecklistSourcePanel';
import CopyChecklistWindowDescription from './CopyChecklistWindowDescription';

// The "Checklist" sub-tab: the read view plus the three ways of filling the checklist in without typing it out,
// Import from Contract, Import from Schedule D/E and Copy Checklist from Another Opportunity. Five panels:
//   - FileUpload (pick + read the contract) then EditSecurityChecklist - the SIDE-BY-SIDE review, since the two
//     sides differ: the checklist as it stands vs. what the contract said. The file itself is stored before the
//     user confirms, as it must be retained even if the review is abandoned. When nothing could be read, EditItem
//     instead: the ordinary edit wizard, to fill the checklist in by hand.
//   - Import from Schedule D/E has no first panel: the button reads the Opportunity's latest Schedule D/E
//     submission and goes straight to the same review, with its own labels (CD-5441).
//   - CopyFrom (pick the source Opportunity) then CopySecurityChecklist - the ORDINARY edit wizard pre-filled
//     from the source, as "Copy Service From" does it. No compare step: both sides would show the same values.
// No flow needs mapping (each first step ends with a checklist resource, so the constructor does it), and none
// can touch completion entries or existing retention details - those live in their own add-only logs. Like the
// edit wizard, each may add a new retention detail, which the save appends to that log.
export default function OpportunitySecurityChecklistPanel({
  opportunityId,
  clientId,
  actualEndOn,
  statusCode,
  scheduleEdLinkSent,
  rawChecklist,
  checklist,
  reloadChecklist,
}) {
  const [shownPanel, setShownPanel] = useState(ShownPanelsEnum.None);
  // The import review's two datasets: the checklist as it is now (left) and the one being saved (right).
  const [reviewData, setReviewData] = useState(null);
  // The copy flow's single dataset: the checklist to be created here, pre-filled from the chosen source.
  const [copiedItem, setCopiedItem] = useState(null);
  // An import that read nothing: the checklist as it stands (or a blank one) for the user to fill in by hand.
  const [manualItem, setManualItem] = useState(null);
  // Bumped after a successful import or copy so ShowEntityData re-reads the checklist.
  const [refreshToken, setRefreshToken] = useState(0);

  const checklistExists = !!checklist?.OpportunitySecurityChecklistId;
  // The checklist GET carries the Opportunity's end date only once a checklist exists, so a new one takes it from
  // the Opportunity itself. The Retain Until date is measured against it.
  const opportunityActualEndOn = rawChecklist?.actualEndOn ?? actualEndOn ?? null;

  //#region Panel state
  const showPanel = useCallback((panelId) => {
    setShownPanel((current) => (panelId === current ? ShownPanelsEnum.None : panelId));
  }, []);

  const closePanel = useCallback(() => {
    setShownPanel(ShownPanelsEnum.None);
    setReviewData(null);
    setCopiedItem(null);
    setManualItem(null);
  }, []);

  // The tab owns the fetch, so re-read FIRST and only then bump the token, or the stale checklist is re-shown.
  const refreshAfterSave = useCallback(async () => {
    closePanel();
    await reloadChecklist();
    setRefreshToken((token) => token + 1);
  }, [closePanel, reloadChecklist]);

  /**
   * ShowEntityData's refresh button calls this with clearCache=true, and it has to go through the tab's loader:
   * the checklist is fetched once and shared with every sub-tab, so a refresh must update all of them. Ordinary
   * (re)mounts get the already-fetched prop.
   */
  const getChecklist = useCallback(
    (id, clearCache) => (clearCache ? reloadChecklist() : Promise.resolve(rawChecklist)),
    [reloadChecklist, rawChecklist]
  );
  //#endregion

  //#region Import sources
  // The two imports differ only in these: the heading their notes go under, the fields they can fill (and so
  // caption when they found nothing), the review they open, and its wording.
  const contractImport = {
    getNotesHeading: () => `From contract import (${new Date().toISOString().substring(0, 10)}):`,
    coveredFields: ImportCoveredChecklistFieldsArray,
    reviewDescription: ImportContractReviewWindowDescription,
    nothingFoundWarning: 'Nothing could be read from this document. Fill in the checklist manually.',
    replaceQuestion:
      'This Opportunity already has a Security Checklist. Values read from the contract will replace the ' +
      'ones shown in the review step — anything you leave as it is stays unchanged. Continue?',
  };
  const scheduleEdImport = {
    // Dated by the submission: that is when the client gave these answers.
    getNotesHeading: (extraction) =>
      `From Schedule D/E (${String(extraction?.submittedOnUtc ?? new Date().toISOString()).substring(0, 10)}):`,
    coveredFields: ScheduleEdImportCoveredChecklistFieldsArray,
    reviewDescription: ImportScheduleEdReviewWindowDescription,
    nothingFoundWarning:
      'The Schedule D/E submission holds no answer for the checklist. Fill in the checklist manually.',
    replaceQuestion:
      'This Opportunity already has a Security Checklist. Answers from the Schedule D/E submission will replace ' +
      'the values shown in the review step — anything you leave as it is stays unchanged. Continue?',
  };
  //#endregion

  //#region Extracted values
  /** Null, blank and an empty list are the extraction's "the contract didn't state it" markers. */
  function isExtractedValue(value) {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    return !(Array.isArray(value) && value.length === 0);
  }

  /**
   * The contract gives the retention as days after the services end; the checklist stores a date. It counts from
   * the Opportunity's actual end, at least one day after it (the checklist's own minimum), and is left out - for the
   * user to enter - when there is no end date or the result would not be later than today.
   */
  function getRetainUntilFromExtraction(extraction) {
    const days = extraction?.dataRetentionDaysAfterEnd;
    if (typeof days !== 'number' || !opportunityActualEndOn) {
      return null;
    }

    // Date parts only, shifted with Date.UTC, so neither the time of day nor the time zone can move the day.
    const [year, month, day] = String(opportunityActualEndOn).substring(0, 10).split('-').map(Number);
    const retainUntil = new Date(Date.UTC(year, month - 1, day + Math.max(days, 1))).toISOString().substring(0, 10);
    const today = new Date().toISOString().substring(0, 10);

    return retainUntil > today ? retainUntil : null;
  }

  /**
   * The extracted lines the notes do not already hold, compared trimmed and case-insensitively. The extraction is
   * told what the notes say, but can still return one of their lines word for word; '' when nothing is new.
   */
  function getLinesNotInNotes(details, notes) {
    if (!isExtractedValue(details)) {
      return '';
    }

    const noteLines = new Set(
      String(notes ?? '')
        .split('\n')
        .map((line) => line.trim().toLowerCase())
    );
    return String(details)
      .split('\n')
      .filter((line) => line.trim() !== '' && !noteLines.has(line.trim().toLowerCase()))
      .join('\n');
  }

  /** True when the document yielded anything at all. */
  function hasExtractedValues(extraction) {
    return Object.values(extraction ?? {}).some(isExtractedValue);
  }

  /**
   * Copies onto the item every field the import actually yielded. The extraction is a checklist resource, so
   * both sides of the copy are ordinary items and only the constructor knows the resource mapping. Fields the
   * source did not state are skipped, so a partial extraction never blanks existing values.
   */
  function applyExtraction(extraction, importSource) {
    const editedItem = checklistExists
      ? new OpportunitySecurityChecklistItem(rawChecklist)
      : new OpportunitySecurityChecklistItem();
    const extractedItem = new OpportunitySecurityChecklistItem(extraction);
    extractedItem.DataRetainUntilUtc = getRetainUntilFromExtraction(extraction);

    Object.entries(extractedItem).forEach(([field, value]) => {
      if (isExtractedValue(value)) {
        editedItem[field] = value;
      }
    });

    // Obligations the checklist has no question for are appended to its notes, never written over them.
    const uncoveredDetails = getLinesNotInNotes(extraction?.uncoveredSecurityDetails, editedItem.Comment);
    if (isExtractedValue(uncoveredDetails)) {
      const heading = importSource.getNotesHeading(extraction);
      editedItem.Comment = [editedItem.Comment, heading, uncoveredDetails].filter(isExtractedValue).join('\n');
    }

    // The covered fields the source said nothing about: the review captions these (e.g. "Not found during AI
    // extraction") so the reviewer knows the value shown is a default/existing one, not read from the source.
    // Carried on the editable item only, so the caption never shows on the read-only (current checklist) side.
    editedItem.FieldsNotFoundInExtraction = importSource.coveredFields.filter(
      (field) => !isExtractedValue(extractedItem[field])
    );

    return editedItem;
  }

  /**
   * The contract names people, the checklist stores Contact ids: fills the ids in place, matched exactly against
   * this client's contacts. Non-fatal - an unmatched name leaves the field to the user.
   */
  async function resolveRepresentatives(extraction) {
    const fullNames = extraction?.authorizedClientBusinessRepFullNames;
    if (!clientId || !Array.isArray(fullNames) || fullNames.length === 0) {
      return;
    }

    const contacts = await ContactService.GetContactsByClientIdAndFullNames(clientId, fullNames).catch(() => null);
    if (Array.isArray(contacts) && contacts.length > 0) {
      // ContactMultiChoice holds an array of id strings (see its multiSelectOnChange).
      extraction.authorizedClientBusinessRep = contacts.map((contact) => String(contact.contactId));
    }
  }
  //#endregion

  //#region Import flow
  function openReview(extraction, fileName, importSource) {
    // Left: the checklist as it stands, or a BLANK item where there is no row yet (the GET returns an empty
    // resource). Right: the same one with the extracted values on top. ClientId can only come from the
    // Opportunity, and the contact picker needs it to filter.
    const currentItem = checklistExists
      ? new OpportunitySecurityChecklistItem(rawChecklist)
      : new OpportunitySecurityChecklistItem();
    currentItem.OpportunityId = currentItem.OpportunityId ? currentItem.OpportunityId : opportunityId;
    currentItem.ClientId = currentItem.ClientId ? currentItem.ClientId : clientId;

    const editedItem = applyExtraction(extraction, importSource);
    editedItem.OpportunityId = currentItem.OpportunityId;
    editedItem.ClientId = currentItem.ClientId;
    editedItem.ActualEndOn = opportunityActualEndOn;
    // Import-only fields: the contract's file name for the audit comment, and the review's confirmation checkbox.
    editedItem.FileName = fileName;
    editedItem.ImportConfirmed = false;

    setReviewData({ editedItem, currentItem, windowDescription: importSource.reviewDescription });
    setShownPanel(ShownPanelsEnum.EditSecurityChecklist);
  }

  /**
   * Nothing was read from the document: the ordinary edit wizard instead of the review, seeded like the Opportunity
   * row's "Edit Security Checklist" action. Saved as a plain edit, so no contract-import audit comment is written.
   */
  function openManualEdit() {
    const editedItem = checklistExists
      ? new OpportunitySecurityChecklistItem(rawChecklist)
      : new OpportunitySecurityChecklistItem();
    editedItem.OpportunityId = opportunityId;
    editedItem.ClientId = clientId;
    editedItem.ActualEndOn = opportunityActualEndOn;

    setManualItem(editedItem);
    setShownPanel(ShownPanelsEnum.EditItem);
  }

  // Everything between the import's values arriving and the review opening.
  async function confirmThenOpenReview(extraction, fileName, importSource) {
    if (!hasExtractedValues(extraction)) {
      ErrorMessage.SetWarning(importSource.nothingFoundWarning);
      openManualEdit();
      return;
    }

    await resolveRepresentatives(extraction);

    // A stored row always holds values: its contact and notification columns are NOT NULL and wizard-required.
    if (checklistExists) {
      ErrorMessage.SetApprove(importSource.replaceQuestion, () => openReview(extraction, fileName, importSource));
      return;
    }

    openReview(extraction, fileName, importSource);
  }

  // Import from Schedule D/E: reads the latest submission (the microservice refuses when there is none) and opens
  // the same review as a contract import.
  async function importFromScheduleEd() {
    const extraction = await OpportunitySecurityChecklistService.GetScheduleEdImport(opportunityId).catch((error) => ({
      error: error?.message,
    }));
    if (!extraction || extraction.error) {
      ErrorMessage.SetErrorMessage(extraction?.error || 'The Schedule D/E answers could not be read.');
      return;
    }

    await confirmThenOpenReview(extraction, undefined, scheduleEdImport);
  }

  /**
   * Why Import from Schedule D/E is greyed out, or '' when it is not: a link must have gone out, and the Opportunity
   * must be open, as closing it deletes the answers. Issuing the MSA does not matter - the answers stay. Whether the
   * contact has submitted is not on the row, so the microservice says so when not.
   */
  function getScheduleEdImportDisabledReason() {
    if (statusCode === OpportunityStatusEnum.OpportunityWon || statusCode === OpportunityStatusEnum.OpportunityLost) {
      return 'The Opportunity is closed, and closing it removed its Schedule D/E answers.';
    }
    if (scheduleEdLinkSent !== true) {
      return 'No Schedule D/E link has been sent for this Opportunity yet.';
    }
    return '';
  }
  //#endregion

  //#region Copy flow
  /**
   * The picker hands over the chosen Opportunity's checklist plus the row it came from; this turns the two into
   * a NEW checklist for THIS Opportunity holding the source's values, for the edit wizard to open on.
   */
  function openCopiedChecklist(sourceChecklist, source) {
    if (!sourceChecklist?.opportunitySecurityChecklistId) {
      ErrorMessage.SetWarning('That Opportunity no longer has a Security Checklist to copy.');
      closePanel();
      return;
    }

    const editedItem = new OpportunitySecurityChecklistItem(sourceChecklist);

    // What must NOT come across: the source row's identity, and its Retain Until date - that is measured against
    // the source's own end date, so this Opportunity needs one of its own (the wizard's validator asks for it as
    // soon as data retention is defined) validated against ITS ActualEndOn.
    editedItem.OpportunitySecurityChecklistId = '';
    editedItem.OpportunityId = opportunityId;
    editedItem.ClientId = clientId;
    editedItem.ActualEndOn = opportunityActualEndOn;
    editedItem.DataRetainUntilUtc = '';
    editedItem.DataRetainUntilDisplay = '';
    // Retention details stay with the source's log, so the new checklist starts with an empty one.
    editedItem.HasRetentionDetails = false;
    // Copy-only fields: where this came from (the audit comment's only extra over a plain save), the client both
    // belong to, and the acknowledgement checkbox.
    editedItem.SourceOpportunityId = source?.OpportunityId;
    editedItem.SourceOpportunityName = source?.OpportunityName ?? '';
    editedItem.SourceClientName = source?.ClientName ?? '';
    editedItem.CopyConfirmed = false;

    setCopiedItem(editedItem);
    setShownPanel(ShownPanelsEnum.CopySecurityChecklist);
  }
  //#endregion

  //#region Render
  const additionalButtons = [
    {
      label: 'Import from Contract',
      iconName: 'fa-solid fa-file-import',
      type: ButtonTypeEnum.NormalIconLeft,
      color: ButtonColorsEnum.Common,
      iconPosition: IconPositionEnum.Left,
      tooltip: 'Read the Security Checklist out of a contract document',
      // The same gate as editing the checklist (Admin/Developer bypass in RoleService).
      roleKey: 'opportunity_edit',
      onClick: () => showPanel(ShownPanelsEnum.FileUpload),
    },
    {
      label: 'Import from Schedule D/E',
      iconName: 'fa-solid fa-file-signature',
      type: ButtonTypeEnum.NormalIconLeft,
      color: ButtonColorsEnum.Common,
      iconPosition: IconPositionEnum.Left,
      // Greyed out with its reason rather than hidden, like the checklist's other gated actions.
      disabled: getScheduleEdImportDisabledReason() !== '',
      tooltip:
        getScheduleEdImportDisabledReason() || "Fill the Security Checklist in from the client's Schedule D/E answers",
      roleKey: 'opportunity_edit',
      onClick: importFromScheduleEd,
    },
  ];

  // Offered only while this Opportunity has NO checklist: a copy may not overwrite one, which the create-only
  // save path enforces server-side whatever the button does.
  if (!checklistExists) {
    additionalButtons.push({
      label: 'Copy Checklist from Another Opportunity',
      iconName: 'fa-solid fa-copy',
      type: ButtonTypeEnum.NormalIconLeft,
      color: ButtonColorsEnum.Common,
      iconPosition: IconPositionEnum.Left,
      tooltip: 'Copy a completed Security Checklist from another won Opportunity of this client',
      // The same gate as editing the checklist (Admin/Developer bypass in RoleService).
      roleKey: 'opportunity_edit',
      onClick: () => showPanel(ShownPanelsEnum.CopyFrom),
    });
  }

  const renderOnePanel = (panelId, content) => (shownPanel === panelId ? content : null);

  function renderPanels() {
    return (
      <div>
        {renderOnePanel(
          ShownPanelsEnum.FileUpload,
          <GeneralEditWizzardBase
            crudType={CrudTypesEnum.Create}
            editedItem={new ContractImportItem({ OpportunityId: opportunityId, ExistingNotes: checklist?.Comment })}
            clickOnCancel={closePanel}
            // clickOnSave's second argument is the resolved serviceFunction response - the extraction.
            clickOnSave={(edited, extraction) =>
              extraction ? confirmThenOpenReview(extraction, edited?.file?.name, contractImport) : closePanel()
            }
            windowDescription={ImportContractUploadWindowDescription}
            errorCode='OSCP01'
          />
        )}
        {renderOnePanel(
          ShownPanelsEnum.EditSecurityChecklist,
          <GeneralEditWizzardBaseSideBySide
            crudType={checklistExists ? CrudTypesEnum.Update : CrudTypesEnum.Create}
            editedItem={reviewData?.editedItem}
            readOnlyItem={reviewData?.currentItem}
            clickOnCancel={closePanel}
            clickOnSave={refreshAfterSave}
            windowDescription={reviewData?.windowDescription ?? ImportContractReviewWindowDescription}
            errorCode='OSCP02'
          />
        )}
        {renderOnePanel(
          ShownPanelsEnum.EditItem,
          <GeneralEditWizzardBase
            crudType={checklistExists ? CrudTypesEnum.Update : CrudTypesEnum.Create}
            editedItem={manualItem}
            clickOnCancel={closePanel}
            clickOnSave={refreshAfterSave}
            windowDescription={EditOpportunitySecurityChecklistWindowDescription}
            errorCode='OSCP04'
          />
        )}
        {renderOnePanel(
          ShownPanelsEnum.CopyFrom,
          <ChooseChecklistSourcePanel
            opportunityId={opportunityId}
            clickOnCancel={closePanel}
            clickOnSubmit={openCopiedChecklist}
          />
        )}
        {renderOnePanel(
          ShownPanelsEnum.CopySecurityChecklist,
          <GeneralEditWizzardBase
            // Always a create: a checklist is only ever copied onto an Opportunity that has none.
            crudType={CrudTypesEnum.Create}
            editedItem={copiedItem}
            clickOnCancel={closePanel}
            clickOnSave={refreshAfterSave}
            windowDescription={CopyChecklistWindowDescription}
            errorCode='OSCP03'
          />
        )}
      </div>
    );
  }

  return (
    <>
      {renderPanels()}
      <ShowEntityData
        itemId={opportunityId}
        entityName='Opportunity Security Checklist'
        type={OpportunitySecurityChecklistItem}
        // It converts internally (new type(result)), so it needs the raw API resource, not the tab's item.
        serviceGetByIdFunction={getChecklist}
        forceRefresh={refreshToken}
        additionalButtons={additionalButtons}
        embedded
      />
    </>
  );
  //#endregion
}
